import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowDown } from "lucide-react";
import { maskLine, EASE } from "./motion";

const HERO_IMG =
  "https://images.unsplash.com/photo-1718248028293-934f04a578db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxydW5uZXIlMjBzcG9ydHMlMjBhY3Rpb24lMjBwaG90b2dyYXBoeXxlbnwwfHx8fDE3ODY5OTUwNDB8MA&ixlib=rb-4.1.0&q=85";

const marqueeItems = ["Padel", "Running", "Sunglasses", "Golf", "Water Sports", "LATAM", "Caribe"];

const RotatingBadge = () => (
  <div className="absolute -top-10 -left-10 w-28 h-28 lg:w-36 lg:h-36 z-10" data-testid="hero-rotating-badge">
    <svg viewBox="0 0 100 100" className="spin-slow w-full h-full">
      <defs>
        <path id="circlePath" d="M 50, 50 m -38, 0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
      </defs>
      <circle cx="50" cy="50" r="50" fill="#0A0A0A" />
      <text fill="#FFFFFF" fontSize="9.5" fontWeight="700" letterSpacing="2.5">
        <textPath href="#circlePath">ROCKY · LATAM · CARIBE · EST. 2018 ·</textPath>
      </text>
      <circle cx="50" cy="50" r="5" fill="#EAB308" />
    </svg>
  </div>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex flex-col justify-end pt-28 pb-0 hero-grid-bg" data-testid="hero-section">
      <motion.div style={{ y: yText }} className="px-6 lg:px-12 max-w-[1600px] mx-auto w-full relative">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="text-xs sm:text-sm uppercase tracking-[0.3em] font-bold text-black/60 mb-6"
          data-testid="hero-eyebrow"
        >
          <span className="inline-block w-8 h-[2px] bg-[#DB2777] align-middle mr-4" />
          Distribución de marcas deportivas — LATAM &amp; Caribe
        </motion.p>

        <h1 className="font-display font-bold uppercase tracking-tighter leading-[0.88] relative z-10" data-testid="hero-title">
          <span className="block overflow-hidden">
            <motion.span custom={0} variants={maskLine} initial="hidden" animate="visible" className="block text-[17vw] lg:text-[13vw]">
              ROCKY<span className="text-[#DB2777]">.</span>
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span custom={1} variants={maskLine} initial="hidden" animate="visible" className="block text-[7.5vw] lg:text-[5vw]">
              Escalamos <span className="font-serif-accent normal-case tracking-normal text-[#DB2777]">marcas</span>
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span custom={2} variants={maskLine} initial="hidden" animate="visible" className="block text-[7.5vw] lg:text-[5vw]">
              emergentes<span className="text-[#EAB308]">.</span>
            </motion.span>
          </span>
        </h1>

        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 mt-12 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.8 }}
            className="flex flex-col gap-8"
          >
            <p className="max-w-md text-base lg:text-lg leading-relaxed text-black/60" data-testid="hero-subtitle">
              Desarrollamos, posicionamos y distribuimos marcas de accesorios
              deportivos de alto potencial en más de 15 mercados de América
              Latina y el Caribe.
            </p>
            <a href="#manifiesto" className="group inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] font-bold w-fit" data-testid="hero-scroll-cue">
              <span className="w-10 h-10 rounded-full border border-black/20 flex items-center justify-center group-hover:bg-[#0A0A0A] group-hover:border-[#0A0A0A] transition-colors duration-300">
                <ArrowDown className="w-4 h-4 group-hover:text-white group-hover:translate-y-0.5 transition-[color,transform] duration-300" />
              </span>
              Descubre Rocky
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, clipPath: "inset(0 100% 0 0)" }}
            animate={{ opacity: 1, clipPath: "inset(0 0% 0 0)" }}
            transition={{ duration: 1.2, ease: EASE, delay: 1 }}
            className="relative w-full lg:w-[460px] shrink-0"
            data-testid="hero-image-frame"
          >
            <RotatingBadge />
            <div className="relative h-64 lg:h-80 overflow-hidden rounded-sm">
              <motion.img
                src={HERO_IMG}
                alt="Atleta corriendo en acción"
                style={{ y: yImg, scale: 1.25 }}
                className="w-full h-full object-cover"
              />
              <span className="absolute bottom-3 left-3 text-[10px] uppercase tracking-[0.25em] font-bold text-white bg-black/60 backdrop-blur-sm px-3 py-1.5">
                En movimiento desde 2018
              </span>
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="border-y border-black/10 bg-white py-4 relative z-10" data-testid="hero-marquee">
        <Marquee speed={30} autoFill gradient={false}>
          {marqueeItems.map((item) => (
            <span key={item} className="mx-12 flex items-center gap-12 font-display font-semibold uppercase tracking-tight text-lg text-black/70">
              {item}
              <span className="w-2 h-2 bg-[#EAB308] inline-block" />
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  );
};

export default Hero;
